import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ArrowRight,
  Bot,
  Check,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  KeyRound,
  Loader2,
  MinusCircle,
  Paperclip,
  PencilLine,
  RotateCcw,
  X,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { formatDate, formatDateTime } from "@/utils/formatDate";
import {
  useExecuteAronWrite,
  useRejectAronWrite,
  useRetryAronWriteSteps,
} from "../api/aronApi";
import { AronRequisitionModal } from "./AronRequisitionModal";
import { AronScheduledTaskProposal } from "./AronScheduledTaskProposal";
import { entityLink, entityRoute } from "../utils/entityRoute";
import {
  writeChainResult,
  writeCredentials,
  writeEntity,
  writeStepResults,
  writeStepsError,
  type AronPendingWrite,
} from "../types";

// Per-object review modals (one bespoke modal per object type, by design).
function modalKindFor(write: AronPendingWrite): "requisition" | null {
  if (write.method === "POST" && /\/api\/v1\/requisitions\/$/.test(write.path)) {
    return "requisition";
  }
  return null;
}

/** Writes that support an optional chained follow-up on execute. The backend
 * validates + executes the chain as the approver (real DRF permissions apply). */
export function chainOptionFor(
  write: AronPendingWrite,
): { action: string; label: string } | null {
  if (write.method !== "POST") return null;
  if (/\/api\/v1\/finances\/client-invoices\/$/.test(write.path)) {
    return {
      action: "approve",
      label:
        "Po utworzeniu od razu zatwierdź szkic faktury (wymaga uprawnień dyrektora; szkic musi mieć pozycję)",
    };
  }
  if (/\/api\/v1\/finances\/client-invoices\/[^/]+\/line-items\/$/.test(write.path)) {
    return {
      action: "approve",
      label:
        "Po dodaniu pozycji od razu zatwierdź szkic faktury (wymaga uprawnień dyrektora)",
    };
  }
  return null;
}

const ENTITY_LABEL: Record<string, string> = {
  ClientInvoice: "fakturę",
  Order: "zlecenie",
  Requisition: "zapotrzebowanie",
  Project: "przedsięwzięcie",
  AcceptanceProtocol: "protokół",
  ContractorInvoice: "fakturę podwykonawcy",
  EmployeeExpense: "fakturę kosztową",
};

/** Clickable link to the object the executed write created/touched. */
export function WriteEntityLink({ write }: { write: AronPendingWrite }) {
  const entity = writeEntity(write);
  if (!entity) return null;
  const cel = entityLink(entity.type, entity.id);
  if (!cel) return null;
  return (
    <Link
      to={cel.route}
      className="mt-1 inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:underline"
    >
      <ExternalLink size={12} />
      {cel.kind === "detail"
        ? `Otwórz ${ENTITY_LABEL[entity.type] ?? "obiekt"} →`
        : "Pokaż w module →"}
    </Link>
  );
}

/**
 * Login i hasło nowego konta — jedyne miejsce, w którym da się je zobaczyć.
 *
 * API pokazuje hasło RAZ, w odpowiedzi na wykonany zapis, i nigdzie go nie
 * przechowuje. Zanim ten kafelek powstał, zapis przez ARON-a gubił je bezpowrotnie:
 * model dostawał sam status, więc dyktował użytkownikowi hasło zmyślone. Dlatego
 * treść jest tu do skopiowania, a nie do zapamiętania — po odświeżeniu rozmowy
 * zniknie i trzeba będzie wylosować nowe.
 */
export function WriteCredentials({ write }: { write: AronPendingWrite }) {
  const [shown, setShown] = useState(false);
  const creds = writeCredentials(write);
  if (!creds || write.status !== "executed") return null;
  return (
    <div className="mt-2 rounded-md border border-green-300 bg-green-50 px-3 py-2 dark:border-green-800 dark:bg-green-950/30">
      <p className="flex items-center gap-1 text-xs font-medium text-green-800 dark:text-green-300">
        <KeyRound size={12} /> Dane logowania — widoczne TYLKO teraz, skopiuj je
        zaraz
      </p>
      {creds.username && (
        <p className="mt-1 text-xs text-gray-700 dark:text-gray-200">
          Login:{" "}
          <code className="select-all font-mono font-medium">
            {creds.username}
          </code>
        </p>
      )}
      <p className="mt-1 flex items-center gap-2 text-xs text-gray-700 dark:text-gray-200">
        Hasło:{" "}
        <code className="select-all font-mono font-medium">
          {shown ? creds.password : "•".repeat(creds.password.length)}
        </code>
        <button
          type="button"
          onClick={() => setShown((v) => !v)}
          className="text-green-700 hover:text-green-900 dark:text-green-400"
          title={shown ? "Ukryj hasło" : "Pokaż hasło"}
        >
          {shown ? <EyeOff size={13} /> : <Eye size={13} />}
        </button>
      </p>
      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
        Po odświeżeniu rozmowy hasło zniknie — zgubione losuje się od nowa
        (reset hasła na karcie podwykonawcy).
      </p>
    </div>
  );
}

/** One compact "Pole: wartość · Pole: wartość" line for a step's body — the key
 * values (kwota, termin…) must be readable in the card preview, not buried in the
 * raw-JSON toggle ("gdzie jest kwota?" — the fields lived in a hidden follow-up). */
function StepFieldsInline({ body }: { body?: Record<string, unknown> | null }) {
  const entries = Object.entries(body || {}).filter(
    ([, v]) => v !== null && v !== undefined && v !== "",
  );
  if (!entries.length) return null;
  const inline = entries.filter(([, v]) => !isObjectRows(v));
  const tables = entries.filter(([, v]) => isObjectRows(v));
  return (
    <span className="block pl-4 text-[11px] text-gray-500 dark:text-gray-400">
      {inline.map(([k, v], i) => (
        <span key={k}>
          {i > 0 && " · "}
          {FIELD_LABELS[k] ?? rawLabel(k)}:{" "}
          <span className="font-medium text-gray-700 dark:text-gray-200">
            {renderValue(v)}
          </span>
        </span>
      ))}
      {tables.map(([k, v]) => (
        <span key={k} className="block">
          {FIELD_LABELS[k] ?? rawLabel(k)}:
          <ObjectRowsTable rows={v as Record<string, unknown>[]} />
        </span>
      ))}
    </span>
  );
}

/**
 * Pliki, które ten zapis dopnie do obiektu — z JAWNYM pochodzeniem.
 *
 * ARON umie już dopiąć plik, który sam WYTWORZYŁ w maszynie (deliver → ManagedFile),
 * a nie tylko ten, który przysłał człowiek. Bez tej informacji karta wyglądałaby
 * identycznie w obu przypadkach i zatwierdzający wpuszczałby do systemu wytwór
 * maszyny w przekonaniu, że wgrywa własny plik.
 */
export function WriteAttachments({ write }: { write: AronPendingWrite }) {
  const files = write.attachments ?? [];
  if (!files.length) return null;
  return (
    <div className="mt-2 border border-gray-200 bg-gray-50 px-2.5 py-2 dark:border-gray-700 dark:bg-gray-900/40">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        Pliki dopinane do obiektu
      </p>
      <ul className="mt-1 space-y-1">
        {files.map((f) => (
          <li
            key={`${f.field}:${f.attachment_id}`}
            className="flex flex-wrap items-center gap-1.5 text-xs text-gray-700 dark:text-gray-200"
          >
            <Paperclip size={12} className="shrink-0 text-gray-400" />
            <span className="font-medium break-all">{f.filename}</span>
            <span className="font-mono text-[10px] text-gray-400">→ {f.field}</span>
            {f.origin === "aron" ? (
              <span
                className="inline-flex items-center gap-1 border border-amber-300 bg-amber-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-amber-800 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-300"
                title="Plik wytworzony przez ARON-a w maszynie i wydany do tej rozmowy — nie wgrał go człowiek."
              >
                <Bot size={10} /> plik ARON-a
              </span>
            ) : f.origin === "unknown" ? (
              <span className="inline-flex items-center gap-1 border border-red-300 bg-red-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide text-red-700">
                <AlertTriangle size={10} /> pliku brak
              </span>
            ) : (
              <span className="font-mono text-[10px] uppercase tracking-wide text-gray-400">
                Twój plik
              </span>
            )}
          </li>
        ))}
      </ul>
      {files.some((f) => f.origin === "aron") && (
        <p className="mt-1.5 text-[11px] leading-snug text-amber-800 dark:text-amber-300">
          Zapis wpuści do systemu plik, który ARON zrobił sam — obejrzyj go przed
          zatwierdzeniem.
        </p>
      )}
    </div>
  );
}

/** Planned follow-up steps (pending proposal) — what ONE approval will run. */
export function WritePlannedSteps({ write }: { write: AronPendingWrite }) {
  const steps = write.follow_ups ?? [];
  if (!steps.length) return null;
  return (
    <div className="mt-2 border border-gray-200 bg-gray-50 px-2.5 py-2 text-xs dark:border-gray-700 dark:bg-gray-900/40">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        Sekwencja — jedno zatwierdzenie wykona kolejno
      </p>
      <ol className="mt-1 space-y-0.5 text-gray-600 dark:text-gray-300">
        <li>1. {write.description || "Zapis główny"}</li>
        {steps.map((s, i) => (
          <li key={i}>
            {i + 2}. {s.description || `${s.method} ${s.path}`}
            <StepFieldsInline body={s.body} />
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Per-step outcomes after execution (response_summary.steps). */
export function WriteStepResults({ write }: { write: AronPendingWrite }) {
  const steps = writeStepResults(write);
  if (!steps.length) return null;
  const done = steps.filter((s) => s.ok).length;
  return (
    <div className="mt-2 border-t border-gray-100 pt-2 dark:border-gray-700">
      <p className="font-mono text-[10px] font-semibold uppercase tracking-wide text-gray-500">
        Przebieg — {done}/{steps.length} wykonanych
      </p>
      <ul className="mt-1 space-y-1 text-xs">
        {steps.map((s, i) => {
          const route =
            s.ok && s.entity_type && s.entity_id
              ? entityRoute(s.entity_type, s.entity_id)
              : null;
          return (
            <li key={s.index} className="flex items-start gap-1.5">
              <span className="mt-[3px] shrink-0">
                {s.ok ? (
                  <Check size={12} className="text-emerald-600" />
                ) : s.skipped ? (
                  <MinusCircle size={12} className="text-gray-300" />
                ) : (
                  <AlertTriangle size={12} className="text-amber-600" />
                )}
              </span>
              <span className="font-mono text-[10px] leading-[1.35] text-gray-400">
                {i + 1}
              </span>
              <span className="min-w-0 flex-1">
                <span
                  className={
                    s.skipped
                      ? "text-gray-400"
                      : s.ok
                        ? "text-gray-700 dark:text-gray-200"
                        : "text-gray-800 dark:text-gray-100"
                  }
                >
                  {s.description || `${s.method} ${s.path}`}
                </span>
                {!s.ok && !s.skipped && (
                  <span className="block text-[11px] text-amber-700 dark:text-amber-300">
                    {s.detail || `kod ${s.status ?? "?"}`}
                  </span>
                )}
                {s.skipped && (
                  <span className="text-gray-400"> — pominięto</span>
                )}
              </span>
              {route && (
                <Link
                  to={route}
                  className="shrink-0 text-red-600 hover:underline"
                >
                  <ArrowRight size={12} className="inline" /> otwórz
                </Link>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Chain outcome line (shared with the mail panel's CEO card). */
export function ChainResultNote({ write }: { write: AronPendingWrite }) {
  const chain = writeChainResult(write);
  if (!chain) return null;
  if (chain.ok) {
    return (
      <p className="mt-1 flex items-center gap-1 text-xs text-green-700">
        <Check size={12} /> Akcja „{chain.action}” wykonana automatycznie po zapisie.
      </p>
    );
  }
  return (
    <p className="mt-1 flex items-center gap-1 text-xs text-amber-700">
      <AlertTriangle size={12} className="shrink-0" />
      Zapis wykonany, ale akcja „{chain.action}” nie powiodła się:{" "}
      {chain.detail || `kod ${chain.status ?? "?"}`}
    </p>
  );
}

const METHOD_VERB: Record<string, string> = {
  POST: "Utworzenie",
  PATCH: "Aktualizacja",
  PUT: "Aktualizacja",
};

// Polish translation for common API field names. The technical name stays as the
// primary label; the Polish translation is shown beneath it (smaller, in colour) so
// the card is readable for non-technical users without hiding the real field.
const FIELD_LABELS: Record<string, string> = {
  project: "Przedsięwzięcie", contractor: "Podwykonawca", client: "Klient",
  order: "Zlecenie", reporter: "Zgłaszający", assignee: "Przypisane do",
  description: "Opis", priority: "Priorytet", deadline: "Termin", due_date: "Termin",
  title: "Tytuł", name: "Nazwa", short_name: "Nazwa skrócona", status: "Status",
  amount: "Kwota", quantity: "Ilość", value: "Wartość", total_value: "Wartość całkowita",
  line_items: "Pozycje", note: "Notatka", comment: "Komentarz", category: "Kategoria",
  nip: "NIP", regon: "REGON", krs: "KRS", is_vat_payer: "Płatnik VAT",
  is_active: "Aktywny", website: "Strona WWW",
  registered_address: "Adres rejestrowy", registered_city: "Miasto rejestrowe",
  address: "Adres", street: "Ulica", building_number: "Nr budynku",
  apartment_number: "Nr lokalu", city: "Miasto", postal_code: "Kod pocztowy",
  country: "Kraj", contact_person: "Osoba kontaktowa", contact_email: "E-mail kontaktowy",
  contact_phone: "Telefon kontaktowy", email: "E-mail", phone: "Telefon",
  first_name: "Imię", last_name: "Nazwisko", position: "Stanowisko", role: "Rola",
  company: "Firma", start_date: "Data rozpoczęcia", end_date: "Data zakończenia",
  bank_account: "Nr konta",
  items: "Pozycje", unit: "Jednostka", base_rate: "Stawka bazowa",
  high_priority_rate: "Stawka priorytetowa", attachment_required: "Wymaga załącznika",
  unit_value: "Cena jedn.", vat_rate: "Stawka VAT",
  planned_end_date: "Planowane zakończenie", planned_revenue: "Planowany przychód",
  planned_revenue_date: "Data planowanego przychodu", coordinator_id: "Koordynator",
  project_name: "Nazwa przedsięwzięcia", project_short_name: "Skrót przedsięwzięcia",
  client_branch: "Oddział klienta", client_sub_branch: "Pododdział klienta",
  branch_name: "Oddział klienta", sub_branch_name: "Pododdział klienta",
};
const VALUE_LABELS: Record<string, string> = {
  low: "Niski", standard: "Standardowy", high: "Wysoki",
};

function rawLabel(k: string): string {
  const s = k.replace(/_/g, " ");
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function renderValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Tak" : "Nie";
  if (Array.isArray(v)) {
    if (!v.length) return "—";
    return isObjectRows(v) ? `${v.length} poz.` : v.map(renderValue).join(", ");
  }
  if (typeof v === "object") {
    const o = v as Record<string, unknown>;
    // Address-like object → one readable line.
    if (o.street || o.city || o.postal_code) {
      const l1 = [o.street, o.building_number].filter(Boolean).join(" ") +
        (o.apartment_number ? `/${o.apartment_number}` : "");
      const l2 = [o.postal_code, o.city].filter(Boolean).join(" ");
      return [l1.trim(), l2, o.country].filter(Boolean).join(", ");
    }
    return Object.entries(o)
      .filter(([, val]) => val !== null && val !== "")
      .map(([k, val]) => `${FIELD_LABELS[k] ?? rawLabel(k)}: ${val}`)
      .join(", ") || "—";
  }
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return formatDate(s);
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(s)) return formatDateTime(s);
  return VALUE_LABELS[s] ?? s;
}

/** A list of plain objects — e.g. ``items`` of POST …/line-items/bulk/ (the whole
 * price list in one write). The approver has to SEE the rows, not a count. */
function isObjectRows(v: unknown): v is Record<string, unknown>[] {
  return (
    Array.isArray(v) &&
    v.length > 0 &&
    v.every((x) => x !== null && typeof x === "object" && !Array.isArray(x))
  );
}

/** One nested table: a row per object, a column per key (first-seen order). The
 * approver of a 12-position price list reads names and rates here instead of the
 * raw JSON. Sygnał META-ARON 2026-09-09 (cały cennik jednym zapisem). */
function ObjectRowsTable({ rows }: { rows: Record<string, unknown>[] }) {
  const keys: string[] = [];
  for (const row of rows) {
    for (const k of Object.keys(row)) if (!keys.includes(k)) keys.push(k);
  }
  return (
    <div className="mt-0.5 overflow-x-auto">
      <table className="w-full text-[11px]">
        <thead>
          <tr className="text-left text-[10px] uppercase tracking-wide text-gray-400">
            <th className="py-0.5 pr-2 font-medium">#</th>
            {keys.map((k) => (
              <th key={k} className="py-0.5 pr-3 font-medium whitespace-nowrap">
                {FIELD_LABELS[k] ?? rawLabel(k)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
          {rows.map((row, i) => (
            <tr key={i}>
              <td className="py-0.5 pr-2 align-top text-gray-400">{i + 1}</td>
              {keys.map((k) => (
                <td key={k} className="py-0.5 pr-3 align-top break-words">
                  {renderValueNode(row[k])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="mt-0.5 text-[10px] text-gray-400">{rows.length} poz.</p>
    </div>
  );
}

/** ``renderValue`` for JSX: lists of objects become a nested table. */
function renderValueNode(v: unknown): ReactNode {
  return isObjectRows(v) ? <ObjectRowsTable rows={v} /> : renderValue(v);
}

function BodyTable({ body }: { body: Record<string, unknown> }) {
  const entries = Object.entries(body || {});
  if (!entries.length) return null;
  return (
    <table className="mt-1.5 w-full text-xs">
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
        {entries.map(([k, v]) => (
          <tr key={k}>
            <td className="w-1/3 py-1 pr-3 align-top whitespace-nowrap">
              <div className="font-mono text-[11px] text-gray-500">{rawLabel(k)}</div>
              {FIELD_LABELS[k] && (
                <div className="text-[10px] text-gray-400">{FIELD_LABELS[k]}</div>
              )}
            </td>
            <td className="py-1 align-top font-medium text-gray-800 dark:text-gray-100 break-words">
              {renderValueNode(v)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Developer JSON editing (is_developer) ────────────────────────────────────
// A write body is a JSON object. The developer editor and the readable table
// share ONE source of truth per step (a JSON string in `drafts`), so an edit in
// either surface stays in sync. "Semantic" validation on the front = parses AND is
// a JSON object (mirrors the backend's `isinstance(override, dict)` guard).

interface BodyParse {
  value?: Record<string, unknown>;
  error?: string;
}

export function parseBodyJson(raw: string): BodyParse {
  let v: unknown;
  try {
    v = JSON.parse(raw);
  } catch (e) {
    return { error: `Niepoprawny JSON: ${(e as Error).message}` };
  }
  if (v === null || typeof v !== "object" || Array.isArray(v)) {
    return { error: "Ciało musi być obiektem JSON ({ … })." };
  }
  return { value: v as Record<string, unknown> };
}

/** Readable field table where scalar fields are inline-editable (developers). */
function EditableBodyTable({
  body,
  onFieldChange,
}: {
  body: Record<string, unknown>;
  onFieldChange: (key: string, value: unknown) => void;
}) {
  const entries = Object.entries(body || {});
  if (!entries.length) {
    return <p className="mt-1.5 text-xs text-gray-400">Puste ciało (brak pól).</p>;
  }
  const inputCls =
    "w-full max-w-md border border-gray-300 bg-white px-1.5 py-0.5 text-xs text-gray-800 focus:border-red-500 focus:outline-none dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100";
  return (
    <table className="mt-1.5 w-full text-xs">
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
        {entries.map(([k, v]) => (
          <tr key={k}>
            <td className="w-1/3 py-1 pr-3 align-top whitespace-nowrap">
              <div className="font-mono text-[11px] text-gray-500">{rawLabel(k)}</div>
              {FIELD_LABELS[k] && (
                <div className="text-[10px] text-gray-400">{FIELD_LABELS[k]}</div>
              )}
            </td>
            <td className="py-1 align-top">
              {typeof v === "boolean" ? (
                <select
                  value={v ? "true" : "false"}
                  onChange={(e) => onFieldChange(k, e.target.value === "true")}
                  className={inputCls}
                >
                  <option value="true">Tak</option>
                  <option value="false">Nie</option>
                </select>
              ) : typeof v === "number" ? (
                <input
                  type="text"
                  inputMode="decimal"
                  value={String(v)}
                  onChange={(e) => {
                    const t = e.target.value.trim();
                    const n = Number(t);
                    onFieldChange(k, t !== "" && Number.isFinite(n) ? n : e.target.value);
                  }}
                  className={inputCls}
                />
              ) : typeof v === "string" ? (
                v.length > 48 ? (
                  <textarea
                    value={v}
                    onChange={(e) => onFieldChange(k, e.target.value)}
                    rows={Math.min(6, Math.max(2, Math.ceil(v.length / 60)))}
                    className={inputCls}
                  />
                ) : (
                  <input
                    type="text"
                    value={v}
                    onChange={(e) => onFieldChange(k, e.target.value)}
                    className={inputCls}
                  />
                )
              ) : (
                <span className="text-gray-500">
                  {renderValueNode(v)}{" "}
                  <span className="text-[10px] text-gray-400">(edytuj w „surowe dane”)</span>
                </span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

interface EditStep {
  isMain: boolean;
  method: string;
  path: string;
  description: string;
}

/** The "surowe dane" panel: EVERY step (main + follow-ups) expandable; each step's
 * raw JSON is a textarea when editable, otherwise read-only. */
function RawStepsEditor({
  steps,
  drafts,
  parsed,
  editable,
  onDraftChange,
}: {
  steps: EditStep[];
  drafts: string[];
  parsed: BodyParse[];
  editable: boolean;
  onDraftChange: (index: number, value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const multi = steps.length > 1;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-2 inline-flex items-center gap-1 border border-gray-200 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wide text-gray-500 transition-colors hover:border-gray-400 hover:text-gray-700"
      >
        {open
          ? "− ukryj surowe dane"
          : `+ surowe dane${multi ? ` (${steps.length} kroków)` : ""}`}
      </button>
      {open && (
        <div className="mt-1 space-y-2">
          {steps.map((s, i) => (
            <div key={i}>
              {multi && (
                <div className="font-mono text-[10px] break-all text-gray-500">
                  {`Krok ${i + 1}/${steps.length} · ${s.method} ${s.path}`}
                  {s.description ? ` — ${s.description}` : ""}
                </div>
              )}
              {editable ? (
                <textarea
                  value={drafts[i]}
                  spellCheck={false}
                  onChange={(e) => onDraftChange(i, e.target.value)}
                  rows={Math.min(16, Math.max(4, drafts[i].split("\n").length))}
                  className="mt-0.5 w-full bg-gray-900 p-2 font-mono text-[11px] leading-relaxed text-gray-100 focus:outline-none focus:ring-1 focus:ring-red-500"
                />
              ) : (
                <pre className="mt-0.5 max-h-48 overflow-auto bg-gray-900 p-2 text-[11px] leading-relaxed text-gray-100">
                  {drafts[i]}
                </pre>
              )}
              {editable && parsed[i]?.error && (
                <p className="mt-0.5 flex items-center gap-1 text-[11px] text-red-600">
                  <AlertTriangle size={11} className="shrink-0" /> {parsed[i].error}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export interface WriteBodyEdit {
  /** Readable table (editable for developers) — render where BodyTable used to be. */
  tableNode: ReactNode;
  /** Raw per-step JSON panel with its own toggle — render below the table. */
  rawNode: ReactNode;
  /** All steps parse to JSON objects (approve is blocked when false). */
  valid: boolean;
  /** Something was edited — send the overrides on execute. */
  dirty: boolean;
  /** Edited main body (undefined when unchanged → backend keeps the stored value). */
  body?: Record<string, unknown>;
  /** Edited dependent steps, full step objects (undefined when unchanged). */
  followUps?: Record<string, unknown>[];
}

/** Shared write-body view/editor used by both the owner card and the CEO card.
 * ``editable`` gates the developer editing affordances; the all-steps view shows
 * regardless. */
export function useWriteBodyEdit(
  write: AronPendingWrite,
  editable: boolean,
): WriteBodyEdit {
  const followUps = write.follow_ups ?? [];
  const steps: EditStep[] = [
    { isMain: true, method: write.method, path: write.path, description: write.description },
    ...followUps.map((f) => ({
      isMain: false,
      method: f.method,
      path: f.path,
      description: f.description,
    })),
  ];
  const initialDrafts = [
    JSON.stringify(write.body ?? {}, null, 2),
    ...followUps.map((f) => JSON.stringify(f.body ?? {}, null, 2)),
  ];
  const [drafts, setDrafts] = useState<string[]>(initialDrafts);

  const parsed = drafts.map(parseBodyJson);
  const valid = parsed.every((p) => !p.error);
  const bodyDirty = drafts[0] !== initialDrafts[0];
  const followUpsDirty = drafts.slice(1).some((d, i) => d !== initialDrafts[i + 1]);

  const setDraft = (index: number, value: string) =>
    setDrafts((prev) => prev.map((d, i) => (i === index ? value : d)));

  const setMainField = (key: string, value: unknown) => {
    const cur = parseBodyJson(drafts[0]).value;
    if (!cur) return; // main JSON currently invalid — edit via the raw panel
    setDraft(0, JSON.stringify({ ...cur, [key]: value }, null, 2));
  };

  const mainValue = parsed[0]?.value;
  const tableNode = editable ? (
    mainValue ? (
      <EditableBodyTable body={mainValue} onFieldChange={setMainField} />
    ) : (
      <p className="mt-1.5 flex items-center gap-1 text-xs text-red-600">
        <AlertTriangle size={12} className="shrink-0" /> Popraw JSON w „surowe dane”,
        aby edytować pola.
      </p>
    )
  ) : (
    <BodyTable body={write.body} />
  );

  const rawNode = (
    <RawStepsEditor
      steps={steps}
      drafts={drafts}
      parsed={parsed}
      editable={editable}
      onDraftChange={setDraft}
    />
  );

  return {
    tableNode,
    rawNode,
    valid,
    dirty: bodyDirty || followUpsDirty,
    body: valid && bodyDirty ? mainValue : undefined,
    followUps:
      valid && followUpsDirty && followUps.length > 0
        ? followUps.map((f, i) => ({ ...f, body: parsed[i + 1].value }) as Record<string, unknown>)
        : undefined,
  };
}

export function StatusBadge({
  status,
  partial = false,
}: {
  status: AronPendingWrite["status"];
  /** Main write executed, a dependent step did not — never show a plain green
   *  "Wykonano" for a half-finished object. */
  partial?: boolean;
}) {
  const map: Record<AronPendingWrite["status"], { label: string; cls: string }> = {
    pending: {
      label: "Czeka na zatwierdzenie",
      cls: "border-red-300 bg-red-50 text-red-700",
    },
    approved: {
      label: "Zatwierdzanie…",
      cls: "border-red-300 bg-red-50 text-red-700",
    },
    executed: {
      label: "Wykonano",
      cls: "border-emerald-300 bg-emerald-50 text-emerald-700",
    },
    failed: {
      label: "Błąd wykonania",
      cls: "border-red-400 bg-red-50 text-red-700",
    },
    rejected: { label: "Odrzucono", cls: "border-gray-300 bg-gray-50 text-gray-500" },
    withdrawn: {
      label: "Wycofano (ARON)",
      cls: "border-gray-300 bg-gray-50 text-gray-500",
    },
  };
  const s =
    partial && status === "executed"
      ? {
          label: "Wykonano częściowo",
          cls: "border-amber-300 bg-amber-50 text-amber-800",
        }
      : map[status];
  return (
    <span
      className={`border px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wide ${s.cls}`}
    >
      {s.label}
    </span>
  );
}

/** "Sequence broke" banner + one-click retry of the steps that did not run. */
export function WriteStepsRetry({
  conversationId,
  write,
}: {
  conversationId: string;
  write: AronPendingWrite;
}) {
  const retry = useRetryAronWriteSteps();
  const note = writeStepsError(write);
  if (!note) return null;
  // The backend note restates every step verbatim — the same wall of text the
  // step list right above already shows, line by line. Lead with the count,
  // keep the original message one click away.
  const steps = writeStepResults(write);
  const failed = steps.filter((s) => !s.ok && !s.skipped).length;
  const skipped = steps.filter((s) => s.skipped).length;
  const headline = steps.length
    ? `Sekwencja nie doszła do końca — nieudane: ${failed}, pominięte: ${skipped}. Obiekt jest niekompletny.`
    : note;
  return (
    <div className="mt-2 border border-amber-300 bg-amber-50 px-2.5 py-2 dark:border-amber-900/60 dark:bg-amber-950/30">
      <p className="flex items-start gap-1.5 text-xs font-medium text-amber-900 dark:text-amber-200">
        <AlertTriangle size={13} className="mt-px shrink-0" /> {headline}
      </p>
      <button
        type="button"
        disabled={retry.isPending}
        onClick={() => retry.mutate({ conversationId, writeId: write.id })}
        className="mt-2 inline-flex items-center gap-1.5 bg-amber-600 px-2.5 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
      >
        {retry.isPending ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <RotateCcw size={12} />
        )}
        Ponów nieudane kroki
      </button>
      {steps.length > 0 && (
        <details className="mt-1.5">
          <summary className="cursor-pointer select-none text-[11px] text-amber-800 underline dark:text-amber-300">
            Pełny komunikat serwera
          </summary>
          <p className="mt-1 text-[11px] leading-relaxed text-amber-800 dark:text-amber-300">
            {note}
          </p>
        </details>
      )}
      {retry.isError && (
        <p className="mt-1.5 text-[11px] text-red-700">
          Nie udało się ponowić kroków — spróbuj ponownie lub popraw dane ręcznie.
        </p>
      )}
    </div>
  );
}

/**
 * The human-in-the-loop write gate. ARON only ever *proposes* a write — nothing is
 * sent to the database until the user clicks "Zatwierdź i wykonaj", at which point
 * the backend executes it as the user, fail-closed against the write allow-list.
 */
export function AronWriteProposal({
  conversationId,
  write,
  onResolved,
  readOnly = false,
}: {
  conversationId: string;
  write: AronPendingWrite;
  /** Called after a successful execute/reject — lets embedding views (e.g. the
   * mail panel, which uses its own queries) refresh their caches immediately. */
  onResolved?: () => void;
  /** Podgląd cudzej rozmowy (audyt CEO+DEV): karta pokazuje CO ARON zaproponował
   * i jak się to skończyło, ale bez przycisków decyzji i bez edycji ciała zapisu.
   * Decyzję i tak podejmuje właściciel wątku — serwer odrzuca każdą inną. */
  readOnly?: boolean;
}) {
  // Scheduled-task proposals render as their own card (different fields, an
  // explicit warning for autonomous write_scope, link to /aron/tasks).
  if (write.kind === "schedule_task") {
    return (
      <AronScheduledTaskProposal
        conversationId={conversationId}
        write={write}
        onResolved={onResolved}
        readOnly={readOnly}
      />
    );
  }
  return (
    <AronApiWriteProposal
      conversationId={conversationId}
      write={write}
      onResolved={onResolved}
      readOnly={readOnly}
    />
  );
}

function AronApiWriteProposal({
  conversationId,
  write,
  onResolved,
  readOnly = false,
}: {
  conversationId: string;
  write: AronPendingWrite;
  onResolved?: () => void;
  readOnly?: boolean;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [chainChecked, setChainChecked] = useState(false);
  const execute = useExecuteAronWrite();
  const reject = useRejectAronWrite();
  const busy = execute.isPending || reject.isPending;
  const pending = write.status === "pending";
  const isDeveloper = useAuthStore((s) => !!s.user?.is_developer);
  // W podglądzie cudzej rozmowy ciała zapisu nie edytujemy — to cudza propozycja.
  const bodyEdit = useWriteBodyEdit(write, isDeveloper && pending && !readOnly);
  const modalKind = modalKindFor(write);
  const stepCount = write.follow_ups?.length ?? 0;
  // A proposed sequence defines the whole flow — the ad-hoc chain checkbox
  // applies only to single writes.
  const chainOption = stepCount ? null : chainOptionFor(write);

  // „Kod odpowiedzi ?" — dosłownie tyle widział użytkownik, gdy karta zdążyła się
  // przemalować na błąd, zanim dojechały szczegóły z serwera: ani kodu, ani treści,
  // ani informacji, że to stan przejściowy. Znak zapytania nie jest komunikatem.
  const failedReason =
    write.status === "failed"
      ? String(
          write.response_summary?.error_detail ||
            write.response_summary?.denied_reason ||
            "",
        ) ||
        (write.response_status
          ? `Serwer odrzucił zapis (kod ${write.response_status}), nie podając treści błędu.`
          : "Zapis się nie powiódł. Szczegóły z serwera jeszcze nie dotarły — odśwież za chwilę.")
      : "";
  const stepsError = writeStepsError(write);

  // One accent stripe carries the state. The card used to be red end-to-end for
  // every status — a finished write looked exactly as alarming as a failed one.
  const accent =
    write.status === "failed"
      ? "border-l-red-600"
      : write.status === "executed"
        ? stepsError
          ? "border-l-amber-500"
          : "border-l-emerald-500"
        : write.status === "rejected" || write.status === "withdrawn"
          ? "border-l-gray-300"
          : "border-l-red-600";

  return (
    <div
      className={`border border-gray-200 border-l-[3px] bg-white dark:border-gray-700 dark:bg-gray-900 ${accent}`}
    >
      <div className="flex items-start gap-2.5 px-3 py-2.5">
        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400">
          <PencilLine size={13} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-gray-500">
              Propozycja zapisu · {METHOD_VERB[write.method] || write.method}
            </span>
            <StatusBadge status={write.status} partial={!!stepsError} />
          </div>
          {write.description && (
            <p className="mt-1 text-sm leading-snug text-gray-800 dark:text-gray-100">
              {write.description}
            </p>
          )}
          {/* Readable Polish table of the proposed fields — inline-editable for
              developers; static for everyone else. */}
          {bodyEdit.tableNode}
          <WriteAttachments write={write} />
          {pending && <WritePlannedSteps write={write} />}
          {/* Raw JSON of EVERY step (main + follow-ups), expandable + editable (dev). */}
          {bodyEdit.rawNode}
          {failedReason && (
            <p className="mt-2 flex items-start gap-1.5 border border-red-200 bg-red-50 px-2 py-1.5 text-xs text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300">
              <AlertTriangle size={13} className="mt-px shrink-0" /> {failedReason}
            </p>
          )}
          <WriteStepResults write={write} />
          {!readOnly && <WriteStepsRetry conversationId={conversationId} write={write} />}
          <ChainResultNote write={write} />
          <WriteCredentials write={write} />
          {write.status === "executed" && <WriteEntityLink write={write} />}
        </div>
      </div>

      {pending && !readOnly && (
        <div className="border-t border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800/50">
          {chainOption && (
            <label className="mb-2 flex cursor-pointer items-start gap-2 text-xs text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={chainChecked}
                onChange={(e) => setChainChecked(e.target.checked)}
                className="mt-0.5 accent-red-600"
              />
              {chainOption.label}
            </label>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() =>
                reject.mutate(
                  { conversationId, writeId: write.id },
                  { onSuccess: () => onResolved?.() },
                )
              }
              className="inline-flex items-center gap-1.5 border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-600 transition-colors hover:border-gray-400 hover:text-gray-900 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300"
            >
              {reject.isPending ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
              Odrzuć
            </button>
            {modalKind === "requisition" ? (
              <button
                type="button"
                disabled={busy}
                onClick={() => setModalOpen(true)}
                className="inline-flex items-center gap-1.5 bg-red-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-40"
              >
                <FileText size={13} /> Sprawdź i utwórz
              </button>
            ) : (
              <button
                type="button"
                disabled={busy || !bodyEdit.valid}
                title={!bodyEdit.valid ? "Popraw JSON, aby zatwierdzić" : undefined}
                onClick={() =>
                  execute.mutate(
                    {
                      conversationId,
                      writeId: write.id,
                      body: bodyEdit.body,
                      followUps: bodyEdit.followUps,
                      chainAction:
                        chainOption && chainChecked ? chainOption.action : undefined,
                    },
                    { onSuccess: () => onResolved?.() },
                  )
                }
                className="inline-flex items-center gap-1.5 bg-red-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-40"
              >
                {execute.isPending ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                {stepCount ? `Zatwierdź i wykonaj (${stepCount + 1} kroki)` : "Zatwierdź i wykonaj"}
                {bodyEdit.dirty && " (edytowane)"}
              </button>
            )}
          </div>
        </div>
      )}

      {modalOpen && modalKind === "requisition" && (
        <AronRequisitionModal
          conversationId={conversationId}
          write={write}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}
